import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {MemoryService} from '../../core/services/memory.service';
import {CategoryQueryDto} from './dtos/category-query.dto';
import {Observable} from 'rxjs';
import {CategorySummaryDto} from './dtos/category-summary.dto';
import {environment} from '../../../environments/environment';
import {EmployeeQueryDto} from './dtos/employee-query.dto';
import {EmployeeSummaryDto} from './dtos/employee-summary.dto';
import {TimeSlotQueryDto} from './dtos/time-slot-query.dto';
import {TimeSlotDto} from './dtos/time-slot.dto';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {DateTime} from 'luxon';
import {AppointmentSelectionFormFactory} from './factories/appointment-selection-form.factory';
import {AppointmentSelectionMapper} from './factories/appointment-selection.mapper';
import {AppointmentFiltersModel} from './models/appointment-filters.model';
import {TimeSlotModel} from './models/time-slot.model';
import {ErrorService} from '../../core/services/error.service';
import {TranslocoService} from '@jsverse/transloco';
import {WindowReferenceService} from '../../core/services/window.service';
import {LocalizedTextDto} from '../../shared/dtos/localized-text.dto';
import {PatientSearchDto} from './dtos/patient-search.dto';
import {PatientDto} from '../patient-management/dtos/patient.dto';
import {AddPatientDto} from './dtos/add-patient.dto';

@Injectable({
  providedIn: 'root'
})
export class AppointmentSelectionService {
  http: HttpClient = inject(HttpClient);
  memoryService: MemoryService = inject(MemoryService);
  fb = inject(FormBuilder);
  errorService = inject(ErrorService);
  translocoService = inject(TranslocoService);
  windowSerivce = inject(WindowReferenceService);

  appointmentFiltersForm?: FormGroup;
  monthForm: FormControl = new FormControl(DateTime.utc().startOf('month'));
  dateForm: FormControl = new FormControl();
  timeSlotForm?: FormGroup;

  categorySummaryDtos?: CategorySummaryDto[];
  groupedCategorySummaryDtos?: { categoryGroup: string | undefined, categorySummaryDtos: CategorySummaryDto[] }[];

  employeeSummaryDtos?: EmployeeSummaryDto[];
  timeSlotDtosMap?: Map<string, TimeSlotDto[]>;
  timeSlotModels: TimeSlotModel[] = [];

  timeSlotDto?: TimeSlotDto;

  init() {
    if (!this.appointmentFiltersForm?.value) {
      const appointmentFiltersModel = {
        employeeId: this.memoryService.employeeId ? this.memoryService.employeeId : (this.memoryService.locationDetailDto!.appearanceSettings.hideAnyTherapist ? undefined : 0),
        categoryId: this.memoryService.categoryId ? this.memoryService.categoryId : undefined
      }
      this.appointmentFiltersForm = AppointmentSelectionFormFactory.createAppointmentFiltersForm(this.fb, appointmentFiltersModel);

      if (appointmentFiltersModel.categoryId && !(appointmentFiltersModel.employeeId == undefined)) {
        this.dateForm.setValue(undefined);
        this.loadTimeSlotDtos();
      }
    }

    this.appointmentFiltersForm.valueChanges.subscribe((appointmentFiltersModel: AppointmentFiltersModel) => {
      if (appointmentFiltersModel.categoryId && !(appointmentFiltersModel.employeeId == undefined)) {
        this.dateForm.setValue(undefined);
        this.loadTimeSlotDtos();
      }
    })

    this.dateForm.valueChanges.subscribe((date?: DateTime): void => {
      const isoDate = date?.toISODate();
      if (!isoDate) {
        this.timeSlotModels = [];
        return;
      }

      const timeSlotDtos = this.timeSlotDtosMap?.get(isoDate);
      if (!timeSlotDtos) {
        this.timeSlotModels = [];
        return;
      }

      this.timeSlotModels = this.toUniqueSortedModels(timeSlotDtos);
    })

    if (!this.timeSlotForm?.value) {
      this.timeSlotForm = AppointmentSelectionFormFactory.createTimeSlotForm(this.fb);
    }

    if (!this.categorySummaryDtos) {
      const categoryQueryDto: CategoryQueryDto = {
        locationIds: [this.memoryService.locationDetailDto!.locationId],
        employeeIds: (this.memoryService.employeeId) ? [this.memoryService.employeeId] : undefined
      };
      this.queryCategorySummaries(categoryQueryDto).subscribe({
        next: (categorySummaryDtos: CategorySummaryDto[]) => {
          this.categorySummaryDtos = categorySummaryDtos;
          let groupedCategorySummaryDtoMap = new Map<string | undefined, CategorySummaryDto[]>();
          const language = this.translocoService.getActiveLang();

          for (const categorySummaryDto of categorySummaryDtos) {
            const categoryGroup = this.localizeText(categorySummaryDto.categoryGroup, language) || undefined;

            if (!groupedCategorySummaryDtoMap.has(categoryGroup)) {
              groupedCategorySummaryDtoMap.set(categoryGroup, []);
            }

            groupedCategorySummaryDtoMap.get(categoryGroup)!.push(categorySummaryDto);
          }

          let groupedCategorySummaryDtos: { categoryGroup: string | undefined, categorySummaryDtos: CategorySummaryDto[] }[] = [];

          for (const [categoryGroup, categorySummaryDtos] of groupedCategorySummaryDtoMap) {
            groupedCategorySummaryDtos.push({categoryGroup: categoryGroup, categorySummaryDtos: categorySummaryDtos})
          }

          this.groupedCategorySummaryDtos = groupedCategorySummaryDtos;

        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error, this.translocoService.translate('error.title.categories'));
        }
      });

      if (this.memoryService.categoryId) {
        this.loadEmployeeSummaryDtos(this.memoryService.categoryId);
      }

      if (this.memoryService.employeeId) {
        const employeeQueryDto: EmployeeQueryDto = {
          locationIds: [this.memoryService.locationDetailDto!.locationId],
          employeeIds: [this.memoryService.employeeId],
        };
        this.queryEmployeeSummaries(employeeQueryDto).subscribe({
          next: (employeeSummaryDtos: EmployeeSummaryDto[]) => {
            this.employeeSummaryDtos = employeeSummaryDtos;
          },
          error: (error) => {
            this.errorService.handleError(error, this.translocoService.translate('error.title.employees'));
          }
        });
      }
    }
  }

  resetSelection() {
    this.resetTimeSelection();
    this.appointmentFiltersForm?.reset();
    this.timeSlotDtosMap = undefined;
    this.timeSlotModels = [];
  }

  resetTimeSelection() {
    this.dateForm.reset();
    this.timeSlotForm?.reset();
    this.timeSlotDto = undefined;
  }

  // Slots are queried per patient, so switching patients invalidates everything already loaded.
  // Filters stay: only the dates and times below them are re-fetched.
  reloadTimeSlotDtos() {
    this.resetTimeSelection();
    this.timeSlotDtosMap = undefined;
    this.timeSlotModels = [];

    const appointmentFiltersModel = this.appointmentFiltersForm?.value;
    if (appointmentFiltersModel?.categoryId && !(appointmentFiltersModel.employeeId == undefined)) {
      this.loadTimeSlotDtos();
    }
  }

  loadEmployeeSummaryDtos(categoryId: number) {
    if (this.memoryService.employeeId) {
      return;
    }

    var found = this.categorySummaryDtos?.find(p => p.categoryId == categoryId);
    if (this.windowSerivce.pushEvents) {
      const language = this.translocoService.getActiveLang();
      var payload = {event: 'treatment_selection', treatment: this.localizeText(found?.name, language) ?? ''};
      this.windowSerivce.push(payload);
    }

    const employeeQueryDto: EmployeeQueryDto = {
      locationIds: [this.memoryService.locationDetailDto!.locationId],
      categoryIds: [categoryId],
    };
    if (found?.employeeIds) {
      employeeQueryDto.employeeIds = found.employeeIds;
    }

    this.queryEmployeeSummaries(employeeQueryDto).subscribe({
      next: (employeeSummaryDtos: EmployeeSummaryDto[]) => {
        this.employeeSummaryDtos = employeeSummaryDtos;
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.employees'));
      }
    });
  }

  loadTimeSlotDtos() {
    const appointmentFiltersModel = this.appointmentFiltersForm?.value;
    if (!appointmentFiltersModel) {
      return;
    }

    if (this.windowSerivce.pushEvents && appointmentFiltersModel.employeeId > 0) {
      var found = this.employeeSummaryDtos?.find(p => p.employeeId == appointmentFiltersModel.employeeId);
      var payload = {event: 'therapist_selection', therapist: `${found?.firstName} ${found?.lastName}`};
      this.windowSerivce.push(payload);
    }

    const timeSlotQueryDto: TimeSlotQueryDto = AppointmentSelectionMapper.toTimeSlotQueryDto(this.memoryService.locationId!, this.memoryService.patientId, appointmentFiltersModel);
    this.queryTimeSlots(timeSlotQueryDto).subscribe({
      next: (timeSlotDtos: TimeSlotDto[]) => {
        this.timeSlotDtosMap = this.toTimeSlotDtoMap(timeSlotDtos);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.timeSlots'));
      }
    });
  }

  queryCategorySummaries(categoryQueryDto: CategoryQueryDto): Observable<CategorySummaryDto[]> {
    return this.http.post<CategorySummaryDto[]>(`${environment.cenplexBookingApiBaseUrl}/customers/${this.memoryService.customerId}/categories/query`, categoryQueryDto);
  }

  queryEmployeeSummaries(employeeQueryDto: EmployeeQueryDto): Observable<EmployeeSummaryDto[]> {
    return this.http.post<EmployeeSummaryDto[]>(`${environment.cenplexBookingApiBaseUrl}/customers/${this.memoryService.customerId}/employees/query`, employeeQueryDto);
  }

  queryTimeSlots(timeSlotQueryDto: TimeSlotQueryDto): Observable<TimeSlotDto[]> {
    return this.http.post<TimeSlotDto[]>(`${environment.cenplexBookingApiBaseUrl}/categories/${timeSlotQueryDto.categoryId}/time-slots/query`, timeSlotQueryDto);
  }

  findPatient(payloadDto: PatientSearchDto): Observable<PatientDto[]> {
    return this.http.post<PatientDto[]>(`${environment.cenplexBookingApiBaseUrl}/customers/${this.memoryService.customerId}/patients/search`, payloadDto);
  }

  createPatient(dto: AddPatientDto): Observable<PatientDto> {
    return this.http.post<PatientDto>(`${environment.cenplexBookingApiBaseUrl}/customers/${this.memoryService.customerId}/patients`, dto);
  }

  private toTimeSlotDtoMap(timeSlotDtos: TimeSlotDto[]): Map<string, TimeSlotDto[]> {
    const result = new Map<string, TimeSlotDto[]>();

    for (const timeSlotDto of timeSlotDtos) {
      const key = DateTime.fromISO(timeSlotDto.startTime).setZone('Europe/Zurich').toISODate();
      if (!key) {
        continue;
      }

      const timeSlotDtos = result.get(key) ?? [];
      timeSlotDtos.push(timeSlotDto);
      result.set(key, timeSlotDtos);
    }

    return result;
  }

  private toUniqueSortedModels(timeSlotDtos: TimeSlotDto[]): TimeSlotModel[] {
    const seen = new Set<string>();
    const models: TimeSlotModel[] = [];

    for (const {startTime, endTime} of timeSlotDtos) {
      // key ensures uniqueness by (start,end)
      const key = `${startTime}__${endTime}`;
      if (!seen.has(key)) {
        seen.add(key);
        models.push({startTime, endTime});
      }
    }

    models.sort(
      (a, b) => Date.parse(a.startTime) - Date.parse(b.startTime)
    );

    return models;
  }

  private localizeText(localizedTextDtos: LocalizedTextDto[] | undefined, language: string) {
    if (!localizedTextDtos) {
      return;
    }

    let localizedText = localizedTextDtos.find(text => text.language === language);
    if (!localizedText && localizedTextDtos.length > 0) {
      localizedText = localizedTextDtos[0];
    }

    return localizedText?.value;
  }
}
